import { useState, useRef, useEffect } from "react";
import "./DateFilter.scss";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DateFilterValue {
  from: string;
  to: string;
  label: string;
}

interface DateFilterProps {
  onFilterChange: (filter: DateFilterValue | null) => void;
}

interface Preset {
  id: string;
  label: string;
  from: () => string;
  to: () => string;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

function isoFromDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

function isoStartOfMonth(monthsBack: number = 0): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() - monthsBack, 1)
    .toISOString()
    .split("T")[0];
}

function isoStartOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

// ── Presets ───────────────────────────────────────────────────────────────────

const PRESETS: Preset[] = [
  { id: "today", label: "Today", from: () => todayISO(), to: () => todayISO() },
  {
    id: "week",
    label: "This week",
    from: () => isoFromDaysAgo(6),
    to: () => todayISO(),
  },
  {
    id: "month",
    label: "This month",
    from: () => isoStartOfMonth(0),
    to: () => todayISO(),
  },
  {
    id: "3m",
    label: "Last 3 months",
    from: () => isoStartOfMonth(3),
    to: () => todayISO(),
  },
  {
    id: "ytd",
    label: "Year to date",
    from: () => isoStartOfYear(),
    to: () => todayISO(),
  },
];

// ── Icons ─────────────────────────────────────────────────────────────────────

function CalendarIcon(): JSX.Element {
  return (
    <svg
      className="date-filter__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      className={`date-filter__chevron${open ? " date-filter__chevron--open" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon(): JSX.Element {
  return (
    <svg
      className="date-filter__check"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DateFilter({
  onFilterChange,
}: DateFilterProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const current: Preset | undefined = PRESETS.find(
    (p: Preset) => p.id === activePreset,
  );
  const [tab, setTab] = useState<"presets" | "years">("presets");

  const CURRENT_YEAR = new Date().getFullYear();
  const YEARS: number[] = Array.from(
    { length: 10 },
    (_, i) => CURRENT_YEAR - i,
  );

  useEffect(() => {
    function handleOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  function selectPreset(id: string | null): void {
    setActivePreset(id);
    setActiveYear(null);
    setOpen(false);

    if (id === null) {
      onFilterChange(null);
      return;
    }

    const preset: Preset | undefined = PRESETS.find((p: Preset) => p.id === id);
    if (!preset) return;

    onFilterChange({
      from: preset.from(),
      to: preset.to(),
      label: preset.label,
    });
  }

  function selectYear(year: number): void {
    setActiveYear(year);
    setActivePreset(null);
    setOpen(false);
    onFilterChange({
      from: `${year}-01-01`,
      to: year === CURRENT_YEAR ? todayISO() : `${year}-12-31`,
      label: String(year),
    });
  }

  return (
    <div className="date-filter" ref={ref}>
      <button
        type="button"
        className={`date-filter__trigger${current ? " date-filter__trigger--active" : ""}`}
        onClick={() => setOpen((prev: boolean) => !prev)}
      >
        <CalendarIcon />
        
        <span className="date-filter__label">
          {activePreset
            ? PRESETS.find((p) => p.id === activePreset)?.label
            : activeYear
              ? String(activeYear)
              : "All time"}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="date-filter__dropdown">
          <div className="date-filter__tabs">
            <button
              type="button"
              className={`date-filter__tab${tab === "presets" ? " date-filter__tab--active" : ""}`}
              onClick={() => setTab("presets")}
            >
              Quick
            </button>
            <button
              type="button"
              className={`date-filter__tab${tab === "years" ? " date-filter__tab--active" : ""}`}
              onClick={() => setTab("years")}
            >
              Year
            </button>
          </div>
          <div className="date-filter__list">
            {tab === "presets" && (
              <>
                <button
                  type="button"
                  className={`date-filter__option${
                    activePreset === null ? " date-filter__option--active" : ""
                  }`}
                  onClick={() => selectPreset(null)}
                >
                  All time
                  {activePreset === null && <CheckIcon />}
                </button>

                <hr className="date-filter__divider" />

                {PRESETS.map((p: Preset) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`date-filter__option${
                      activePreset === p.id
                        ? " date-filter__option--active"
                        : ""
                    }`}
                    onClick={() => selectPreset(p.id)}
                  >
                    {p.label}
                    {activePreset === p.id && <CheckIcon />}
                  </button>
                ))}
              </>
            )}
            {tab === "years" && (
              <>
                {YEARS.map((year: number) => (
                  <button
                    key={year}
                    type="button"
                    className={`date-filter__option${activeYear === year ? " date-filter__option--active" : ""}`}
                    onClick={() => selectYear(year)}
                  >
                    {year}
                    {activeYear === year && <CheckIcon />}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
